/**
 * Wrapper que renderiza UM widget centralizado em tela cheia.
 *
 * Usado quando a URL tem `?token=X` — pra que o link copiado do admin
 * (`http://embed.omagroup.com.br/?token=...`) abra a calculadora ocupando
 * toda a viewport, sem nenhum chrome de dev sandbox.
 *
 * Comportamento:
 *   - Carrega o schema pra descobrir `mode` + `theme_image_url`
 *   - mode=branded + imagem: aplica bg full-screen no body (gradient + url)
 *   - mode=calc_only: bg nevoa neutro (paleta OMA)
 *   - Card central com max-width pra desktop, edge-to-edge no mobile
 *   - Sem header/títulos extras — única referência OMA é o "Powered by ..."
 *     do próprio footer do <WidgetShell>.
 */
import { useEffect, useMemo, useState } from 'react';
import { App } from './App';
import { CalculatorClient } from './api/client';
import type { SchemaResponse } from './api/types';

interface Props {
  token: string;
  apiBase: string;
  locale?: string;
}

export function FullScreenWidget({ token, apiBase, locale }: Props) {
  const client = useMemo(
    () => new CalculatorClient({ baseUrl: apiBase, locale }),
    [apiBase, locale],
  );
  const [schema, setSchema] = useState<SchemaResponse | null>(null);
  const [boot, setBoot] = useState<'loading' | 'ready' | 'error'>('loading');

  // Boot leve só pra descobrir tema — o App.tsx vai fazer o próprio fetch
  // depois (idempotente do lado do servidor + cache de 1h pelo browser).
  useEffect(() => {
    const ctrl = new AbortController();
    client.getSchema(token, ctrl.signal)
      .then((s) => { setSchema(s); setBoot('ready'); })
      .catch(() => setBoot('error'));
    return () => ctrl.abort();
  }, [client, token]);

  // Aplica bg no body em modo branded.
  useEffect(() => {
    if (!schema) return;
    const body = document.body;
    const html = document.documentElement;
    const prev = { bg: body.style.backgroundImage, color: body.style.backgroundColor };

    if (schema.mode === 'branded' && schema.theme_image_url) {
      body.style.backgroundImage = `linear-gradient(rgba(41,50,62,0.18), rgba(74,76,57,0.22)), url("${schema.theme_image_url}")`;
      body.style.backgroundSize = 'cover';
      body.style.backgroundPosition = 'center';
      body.style.backgroundAttachment = 'fixed';
      body.style.backgroundColor = '#29323E';
    } else {
      body.style.backgroundColor = '#F4EFE4'; // nevoa
    }
    html.style.minHeight = '100vh';
    body.style.minHeight = '100vh';

    return () => {
      body.style.backgroundImage = prev.bg;
      body.style.backgroundColor = prev.color;
    };
  }, [schema]);

  if (boot === 'loading') {
    return (
      <div className="py-10 flex justify-center w-full">
        <div
          className="size-8 rounded-full border-2 border-floresta/20 border-t-floresta animate-spin"
          aria-label="Carregando"
        />
      </div>
    );
  }
  return <App token={token} apiBase={apiBase} locale={locale} fullScreen />;
}
